import { Button } from "@/components/ui/Button";
import { clearLoadTrace } from "@/lib/loopGuard";

/**
 * Shown when the page has reloaded several times inside a minute without a
 * session appearing. Deliberately does **not** touch Clerk — mounting it again
 * is what continues the loop.
 *
 * The causes are ordered by how often they're the answer, so the first thing
 * read is the first thing to check.
 */
export function RedirectLoop() {
  return (
    <main className="mx-auto grid min-h-dvh max-w-xl place-items-center px-6 py-10">
      <div>
        <img
          src="/logo.svg"
          alt=""
          width={48}
          height={48}
          className="mb-5 rounded-xl"
        />
        <h1 className="text-lg font-semibold">Sign-in is redirecting in a loop</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          The page reloaded several times without settling, so it has stopped
          to avoid spinning forever. This is a sign-in configuration
          problem, not a fault in the directory itself.
        </p>

        <div className="border-border bg-card mt-6 rounded-lg border p-4 text-sm">
          <p className="font-medium">What to check, most likely first</p>
          <ol className="text-muted-foreground mt-2 list-decimal space-y-2 pl-5">
            <li>
              <span className="text-foreground font-medium">
                This exact host is allowed in Clerk.
              </span>{" "}
              Clerk → Configure → Domains → Allowed subdomains must contain{" "}
              <code className="text-foreground">{window.location.hostname}</code>{" "}
              as a literal entry. <code>www</code> counts as a subdomain and is
              not covered by the parent domain being listed.
            </li>
            <li>
              <span className="text-foreground font-medium">
                A stale sign-in from another Ley Choon app.
              </span>{" "}
              Sign-in cookies are shared across <code>*.leychoon.app</code>, and
              a broken one keeps every load trying to resume it. Open this page
              in a private window — if it works there, clear this browser's
              cookies for <code>leychoon.app</code>.
            </li>
            <li>
              <span className="text-foreground font-medium">
                You are on the canonical host.
              </span>{" "}
              The app is served at <code>www.leychoon.app</code>; the bare domain
              should redirect there rather than serve the app itself.
            </li>
            <li>
              <span className="text-foreground font-medium">
                Third-party cookies are not blocked
              </span>{" "}
              for <code>clerk.leychoon.app</code> — strict privacy settings and
              some extensions break the sign-in handshake.
            </li>
          </ol>
        </div>

        <p className="text-muted-foreground mt-4 text-xs">
          The browser console and the network tab will show the redirect: look
          for repeated requests to <code>clerk.leychoon.app</code>.
        </p>

        <Button
          className="mt-5"
          onClick={() => {
            clearLoadTrace();
            window.location.reload();
          }}
        >
          Try again
        </Button>
      </div>
    </main>
  );
}
